import { isCanonicalHost, trackEvent } from './gtm'
import { isInNativeApp } from './nativeBridge'

// Logical event name -> Adjust web app event token. Independent from the native
// lite app tokens (see NATIVE_EVENT_TOKENS in nativeBridge.ts): web and native are
// separate Adjust apps, so the same logical event has a different token on each.
const WEB_EVENT_TOKENS: Record<string, string> = {
  af_login: 'mwbzfi',
  af_registration: 'weltdo',
  af_open_game: 'spz0za',
  login_form_open: 'v3hyru',
  registration_form_init: 'iq22g1',
  logout: 'axmjvi',
  begin_checkout: 'mjeec0',
}

// Web Adjust only runs on the production build, only on the canonical hosts (same
// gate as the other trackers, see isCanonicalHost), and only on plain web. Inside
// the native wrapper the native Adjust SDK handles tracking (via trackNativeEvent),
// so the web SDK must stay dormant to avoid double-counting. Evaluated on every call
// so a late `Fun88Native` injection still keeps web events from firing in-app.
const isAdjustEnabled = () =>
  import.meta.env.MODE === 'prod' && isCanonicalHost() && !isInNativeApp()

// Load the Adjust web SDK on demand (dynamic import) so its ~200KB never lands
// in the entry chunk / critical boot path. loadSdk() is only reached when Adjust
// is enabled (prod + canonical host + not native), so dev, staging, mirror hosts
// and the lite app never fetch it at all. Cached: imported and initialised once
// per session, and every later caller reuses the same instance.
// eslint-disable-next-line @typescript-eslint/consistent-type-imports -- the default export is a value, so a top-level `import type` can't express `typeof <value>`; the runtime import must stay dynamic to keep the SDK off the entry chunk.
type AdjustSdk = typeof import('@adjustcom/adjust-web-sdk').default
let sdkPromise: Promise<AdjustSdk> | null = null
const loadSdk = (): Promise<AdjustSdk> => {
  if (!sdkPromise) {
    sdkPromise = import('@adjustcom/adjust-web-sdk').then(m => m.default)
  }
  return sdkPromise
}

export const initAdjust = () => {
  if (!isAdjustEnabled()) return
  const appToken = import.meta.env.VITE_ADJUST_APP_TOKEN
  if (!appToken) return
  void loadSdk().then(Adjust =>
    Adjust.initSdk({ appToken, environment: 'production' }),
  )
}

type AdjustEventOptions = {
  revenue?: number
  currency?: string
  callbackParams?: { key: string; value: string }[]
}

export const trackAdjustEvent = (
  name: string,
  options: AdjustEventOptions = {},
) => {
  if (!isAdjustEnabled()) return
  const eventToken = WEB_EVENT_TOKENS[name]
  if (!eventToken) return
  void loadSdk().then(Adjust => Adjust.trackEvent({ eventToken, ...options }))
}

// How long a user-facing caller (register / deposit submit) waits for the
// web_uuid before falling back to the fingerprint device id. Counted from the
// call, so without the warm-up below it starts only when the user clicks.
const WEB_UUID_TIMEOUT_MS = 1500

// Cache the SDK's web_uuid resolution so it is computed once per session and
// reused. waitForWebUUID() resolves as soon as the uuid becomes available, so
// the cached promise carries the real value to every later caller without a
// fresh wait. No internal timeout here on purpose: we want the true value
// whenever the SDK answers, never a premature '' poisoning the cache.
let webUuidPromise: Promise<string> | null = null

const resolveWebUUID = (): Promise<string> => {
  if (!webUuidPromise) {
    webUuidPromise = loadSdk()
      .then(Adjust => Adjust.waitForWebUUID())
      .then(uuid => uuid ?? '')
      .catch(() => '')
  }
  return webUuidPromise
}

// Warm up the web_uuid at app bootstrap so the SDK has from page-load (not from
// the deposit click) to produce it. By the time the user submits, the cached
// promise is usually already resolved, so getAdjustWebUUID returns instantly
// instead of racing a 1500ms timer that starts at the click.
//
// Also emits one diagnostic event per session recording how long the SDK took
// and whether it ever produced a uuid — this is how we confirm whether the
// missing web_uuid was a timeout (web_uuid_ready_ms > WEB_UUID_TIMEOUT_MS) or
// the SDK never resolving at all (web_uuid_ok = 0, e.g. blocked by an adblocker).
export const preloadAdjustWebUUID = () => {
  if (!isAdjustEnabled()) return
  const start =
    typeof performance !== 'undefined' ? performance.now() : Date.now()
  void resolveWebUUID().then(uuid => {
    const elapsed =
      (typeof performance !== 'undefined' ? performance.now() : Date.now()) -
      start
    trackEvent('web_uuid_warmup', {
      web_uuid_ok: uuid ? 1 : 0,
      web_uuid_ready_ms: Math.round(elapsed),
    })
  })
}

// Resolve the Adjust web_uuid to attach to register / submitpay meta. Returns ''
// when Adjust isn't running (non-prod, in-app) or doesn't answer in time, so
// callers can fall back to the fingerprint device id.
export const getAdjustWebUUID = (): Promise<string> => {
  if (!isAdjustEnabled()) return Promise.resolve('')
  let timer: ReturnType<typeof setTimeout>
  const timeout = new Promise<string>(resolve => {
    timer = setTimeout(() => resolve(''), WEB_UUID_TIMEOUT_MS)
  })
  return Promise.race([resolveWebUUID(), timeout]).then(uuid => {
    clearTimeout(timer)
    // Empty at the point of use: the uuid still wasn't ready when the user
    // acted. Emit it so the point-of-use miss rate is measurable in analytics
    // alongside the warm-up timing above.
    if (!uuid) trackEvent('web_uuid_miss')
    return uuid
  })
}

// Flat attribution fields, matching the shape the native lite app already posts so
// the backend doesn't need a web-specific branch. The nested `adjust` mirrors the
// native payload but only carries the two fields web can supply (ad_app_token +
// ad_adid); native-only device fields (ad_gps_adid, android_id, ...) are omitted.
// Empty object when there's no attribution to report.
export type AdjustAttributionMeta = {
  tracker_token: string
  tracker_name: string
  network: string
  campaign: string
  adgroup: string
  creative: string
  click_label: string
  adjust: {
    ad_app_token: string
    ad_adid: string
  }
}

// Resolve the Adjust attribution (network / campaign / tracker / etc.) as a flat
// meta fragment to attach to register / submitpay. waitForAttribution settles once
// the SDK gets the attribution from the server, so guard it with a timeout. Returns
// {} when Adjust isn't running or doesn't answer in time.
export const getAdjustAttribution = (): Promise<
  Partial<AdjustAttributionMeta>
> => {
  if (!isAdjustEnabled()) return Promise.resolve({})
  const fromSdk = loadSdk()
    .then(Adjust => Adjust.waitForAttribution())
    .then(attr =>
      attr
        ? {
            tracker_token: attr.tracker_token ?? '',
            tracker_name: attr.tracker_name ?? '',
            network: attr.network ?? '',
            campaign: attr.campaign ?? '',
            adgroup: attr.adgroup ?? '',
            creative: attr.creative ?? '',
            click_label: attr.click_label ?? '',
            adjust: {
              ad_app_token: import.meta.env.VITE_ADJUST_APP_TOKEN ?? '',
              ad_adid: attr.adid ?? '',
            },
          }
        : {},
    )
    .catch(() => ({}))
  const timeout = new Promise<Record<string, never>>(resolve =>
    setTimeout(() => resolve({}), 1500),
  )
  return Promise.race([fromSdk, timeout])
}
